import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled, keyframes, css } from 'styled-components';
import Draggable from 'react-draggable';
import { DrawingCanvas, DrawingMode } from './DrawingCanvas';
import { Placement, Position, Snapshot } from './types';
import { ReactComponent as PlayerOpponentDisk } from './assets/player-opponent.svg';
import { ReactComponent as PlayerOwnDisk } from './assets/player-us.svg';
import { ReactComponent as BallDisk } from './assets/ball.svg';
import { ReactComponent as GoalKeeperDisk } from './assets/player-goalkeeper.svg';
import {
  white,
  yellow,
  handballLightCourt,
  handballLightGoalArea,
  handballLightLines,
  handballLightBehindGoal,
  handballDarkCourt,
  handballDarkGoalArea,
  handballDarkLines,
  handballDarkBehindGoal,
} from './components/colors';
import { MovementMode } from './store/app';
import { PathOverlay, LiveTrail } from './components/plays/PathOverlay';
import {
  MIN_SAMPLE_DISTANCE,
  PieceKind,
  applyDragToPathSet,
} from './utils/path';

const PENALTY_SHOT_LINE_LENGTH = 1;
const PENALTY_SHOT_LINE_DISTANCE = 7;
const GOALAREA_SHOT_LINE_LENGTH = 0.5;
const GOALAREA_SHOT_LINE_DISTANCE = 4;
const LINE_WIDTH = 0.15;
const OUTER_LINE_WIDTH = 0.15;
const GOAL_LINE_X = 1;
const GOAL_WIDTH = 3;
const GOAL_DEPTH = 1;
const POOL_PADDING_X = 1;
const POOL_PADDING_Y = 0.5;
// Court LENGTH used for layout. The real handball court is 40 m, but we render
// it 25% shorter (30 m) for better visibility — this only compresses the empty
// "midfield" between the two goal ends. All goal-area markings (goal, 6 m / 9 m
// areas, 7 m / 4 m lines) use fixed real radii independent of POOL_WIDTH, so
// their geometry is unaffected; only the distance between the two ends shrinks.
const POOL_WIDTH = 30;
const POOL_HEIGHT = 20;
const POOL_FULL_WIDTH = POOL_WIDTH + 2 * POOL_PADDING_X;
const POOL_FULL_HEIGHT = POOL_HEIGHT + 2 * POOL_PADDING_Y;
const FREE_SHOT_AREA_MAX_RADIUS = 10;
const FREE_SHOT_AREA_MIN_RADIUS = 9;
const GOAL_AREA_RADIUS = 6;

const PLAYER_SIZE = 1.2;

const Pool = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
  $colorMode?: 'light' | 'dark';
}>`
  position: relative;
  box-sizing: content-box;
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  display: flex;
  align-items: center;
  justify-content: center;

  /* Sport colour tokens, inherited by all court children (lines, areas).
     Dark mode keeps the original look; light mode matches a real handball
     court (blue floor, orange goal area, white lines). */
  ${({ $colorMode }) =>
    $colorMode === 'dark'
      ? `
        --hb-line: ${handballDarkLines};
        --hb-court: ${handballDarkCourt};
        --hb-goal-area: ${handballDarkGoalArea};
        --hb-behind-goal: ${handballDarkBehindGoal};
      `
      : `
        --hb-line: ${handballLightLines};
        --hb-court: ${handballLightCourt};
        --hb-goal-area: ${handballLightGoalArea};
        --hb-behind-goal: ${handballLightBehindGoal};
      `};
`;

const PositionedDrawingCanvas = styled(DrawingCanvas)<{
  $placement: Placement;
  $ratio: number;
  $isActive: boolean;
}>`
  position: absolute;
  box-sizing: content-box;
  top: ${(props) => `${(props.$placement.y as number) - props.$ratio * 0.1}px`};
  left: ${(props) =>
    `${(props.$placement.x as number) - props.$ratio * 0.1}px`};
  z-index: ${(props) => (props.$isActive ? 20 : 0)};
`;

const PlayArea = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled: boolean;
  $isDarkMode?: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  border-color: var(--hb-line);
  border-width: ${({ $ratio }) => `${$ratio * OUTER_LINE_WIDTH}px`};
  border-style: solid;
  border-radius: ${({ $ratio }) => $ratio * (24 / 80)}px;
  top: ${({ $placement }) => `${$placement.y as number}px`};
  left: ${({ $placement }) => `${$placement.x as number}px`};
  height: ${({ $placement }) => `${$placement.height}px`};
  width: ${({ $placement }) => `${$placement.width}px`};
  z-index: 10;
  background-color: var(--hb-court);
`;

const Goal = styled.div<{
  $placement: Placement;
  $ratio: number;
  $reverse: boolean;
  $halfView: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  border-color: var(--hb-line);
  border-style: solid;
  border-width: ${(props) => `${props.$ratio * OUTER_LINE_WIDTH}px`};
  ${({ $reverse, $halfView }) =>
    $halfView ? '' : $reverse ? `border-left: none;` : `border-right: none;`};
  top: ${(props) => `${props.$placement.y}`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}` : `left: ${x}`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  ${({ $ratio, $halfView, $reverse }) => {
    if ($halfView && !$reverse) {
      return `
        border-top-left-radius: ${$ratio * (24 / 80)}px;
        border-top-right-radius: ${$ratio * (24 / 80)}px;
        border-bottom: none;
      `;
    }
    if (!$halfView && $reverse) {
      return `
        border-top-right-radius: ${$ratio * (24 / 80)}px;
        border-bottom-right-radius: ${$ratio * (24 / 80)}px;
      `;
    }
    if (!$halfView && !$reverse) {
      return `
        border-top-left-radius: ${$ratio * (24 / 80)}px;
        border-bottom-left-radius: ${$ratio * (24 / 80)}px;
      `;
    }
  }}
  z-index: 25;
`;

const BehindGoal = styled.div<{
  $placement: Placement;
  $ratio: number;
  $reverse: boolean;
  $halfView: boolean;
}>`
  position: absolute;
  box-sizing: border-box;
  ${({ $reverse, $halfView }) =>
    $halfView ? '' : $reverse ? `border-left: none;` : `border-right: none;`};
  top: ${(props) => `${props.$placement.y}`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}` : `left: ${x}`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  z-index: 20;
  background-color: var(--hb-behind-goal);
`;

const Line = styled.div<{
  $placement: Placement;
  $reverse?: boolean;
  $dashed?: boolean;
  $color?: string;
  $halfView?: boolean;
  $ratio: number;
}>`
  position: absolute;
  background-color: var(--hb-line);
  top: ${(props) => `${props.$placement.y}px`};
  ${({ $reverse = false, $placement: { x } }) =>
    $reverse ? `right: ${x}px` : `left: ${x}px`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  z-index: 15;
`;

// Rounds only the two corners on the court-facing side, each to a quarter
// ellipse of exactly ($radiusX x $radiusY) px. Because the box is longer than
// 2*radius on its across axis, a STRAIGHT segment remains between the two
// quarter-ellipses (the real handball goal-area / free-throw line: two arcs
// around the posts joined by a straight run). Giving both the 6 m and 9 m areas
// the same-metre radius keeps their straight segments parallel and a constant
// 3 m apart, and makes the straight part grow on wide screens instead of the
// arc ballooning open.
const arcCorners = ($halfView: boolean, $reverse: boolean, r: string) => {
  if ($halfView && !$reverse) {
    // half view: goal at top, area bulges DOWN into the court
    return `
      border-bottom-left-radius: ${r};
      border-bottom-right-radius: ${r};
      border-top: none;
    `;
  }
  if (!$halfView && $reverse) {
    // full view: right goal, area bulges LEFT into the court
    return `
      border-top-left-radius: ${r};
      border-bottom-left-radius: ${r};
      border-right: none;
    `;
  }
  // full view: left goal, area bulges RIGHT into the court
  return `
    border-top-right-radius: ${r};
    border-bottom-right-radius: ${r};
    border-left: none;
  `;
};

const GoalArea = styled.div<{
  $placement: Placement;
  $reverse?: boolean;
  $ratio: number;
  $radiusX: number;
  $radiusY: number;
  $halfView: boolean;
  $colorMode?: 'light' | 'dark';
}>`
  z-index: 15;
  position: absolute;
  border-color: var(--hb-line);
  background-color: var(--hb-goal-area);
  border-style: solid;
  box-sizing: border-box;
  border-width: ${({ $ratio }) => `${LINE_WIDTH * $ratio}px`};
  top: ${(props) => `${props.$placement.y}`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}` : `left: ${x}`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  ${({ $halfView, $reverse = false, $radiusX, $radiusY }) =>
    arcCorners($halfView, $reverse, `${$radiusX}px ${$radiusY}px`)}
`;

const FreeShotArea = styled.div<{
  $placement: Placement;
  $reverse: boolean;
  $ratio: number;
  $radiusX: number;
  $radiusY: number;
  $halfView: boolean;
}>`
  z-index: 15;
  position: absolute;
  border-color: var(--hb-line);
  border-style: dashed;
  box-sizing: border-box;
  border-width: ${({ $ratio }) => `${LINE_WIDTH * $ratio}px`};
  top: ${(props) => `${props.$placement.y}`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}` : `left: ${x}`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  ${({ $halfView, $reverse = false, $radiusX, $radiusY }) =>
    arcCorners($halfView, $reverse, `${$radiusX}px ${$radiusY}px`)}
`;

const pulse = keyframes`
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.3);
    }
`;

const pulseAnimation = css`
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const Player = styled.div<{
  $placement: Placement;
  $halfView: boolean;
  $origin: number;
  $selected?: boolean;
  $number?: string;
}>`
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
  z-index: 50;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "${(props) => props.$number || ''}";
    color: ${yellow};
    font-size: 1rem;
    position: absolute;
    font-weight: bold;
  }

  ${(props) => props.$selected && pulseAnimation}
`;

const Ball = styled(Player)`
  height: ${(props) => `${(props.$placement.height as number) / 2}px`};
  width: ${(props) => `${(props.$placement.width as number) / 2}px`};
`;

const ShotLine = styled.div<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled?: boolean;
  $reverse: boolean;
}>`
  z-index: 16;
  position: absolute;
  box-sizing: border-box;
  top: ${(props) => `${props.$placement.y}`};
  ${({ $reverse, $placement: { x } }) =>
    $reverse ? `right: ${x}` : `left: ${x}`};
  height: ${(props) => `${props.$placement.height}px`};
  width: ${(props) => `${props.$placement.width}px`};
`;

const ShotLineYellow = styled(ShotLine)<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled?: boolean;
  $reverse: boolean;
}>`
  background-color: var(--hb-line);
`;

const ShotLineOrange = styled(ShotLine)<{
  $placement: Placement;
  $ratio: number;
  $halfView: boolean;
  $isDrawingEnabled?: boolean;
  $reverse: boolean;
}>`
  background-color: var(--hb-line);
`;

export const PoolElement: React.FC<{
  isSubscribedUser?: boolean;
  colorMode?: 'light' | 'dark';
  halfView: boolean;
  className?: string;
  poolMaxHeight: number;
  poolMaxWidth: number;
  snapshot: Snapshot;
  isPositionSelectorOpen?: boolean;
  isDrawingEnabled?: boolean;
  drawingMode?: DrawingMode;
  onStopDrawing?: () => void | null;
  onDrawingsCleaned: () => void | null;
  shouldCleanDrawings: boolean;
  onSnapshotUpdate?: (snapshot: Snapshot) => void;
  onNamingPlayer?: (index: number) => void;
  shouldUndoLastDrawing?: boolean;
  onLastDrawingUndone: () => void | null;
  namings?: (string | null)[];
  opponentIncrease: number;
  isStatic?: boolean;
  playName?: string;
  poolRef?: React.RefObject<HTMLElement> | null;
  onStartNamingPlayer?: (index: number) => void;
  selectedPlayer?: number | null;
  namingEditorActive?: boolean;
  backgroundText?: string;
  movementMode?: MovementMode;
  showSavedPaths?: boolean;
}> = ({
  colorMode = 'light',
  halfView,
  className,
  snapshot,
  poolMaxHeight,
  poolMaxWidth,
  isPositionSelectorOpen = true,
  isDrawingEnabled = false,
  drawingMode = DrawingMode.NONE,
  onStopDrawing = null,
  onDrawingsCleaned,
  shouldCleanDrawings,
  onSnapshotUpdate = () => {},
  onNamingPlayer,
  shouldUndoLastDrawing = false,
  onLastDrawingUndone,
  namings,
  opponentIncrease,
  isStatic = false,
  isSubscribedUser = false,
  playName = '',
  poolRef = null,
  selectedPlayer = null,
  onStartNamingPlayer = (index: number) => () => {},
  namingEditorActive = false,
  backgroundText,
  movementMode = MovementMode.LINEAR,
  showSavedPaths = false,
}) => {
  const [poolHeight, setPoolHeight] = useState<number>(POOL_HEIGHT);
  const [poolWidth, setPoolWidth] = useState<number>(POOL_WIDTH);
  const [positionAdjustment, setPositionAdjustment] = useState<number>(1);
  const [ratio, setRatio] = useState<number>(1);
  // Two-axis (non-uniform) fill scales. ratioX scales the horizontal axis,
  // ratioY the vertical one; `ratio` (= min of the two) is the uniform scale
  // kept for round tokens and even line thickness.
  const [ratioX, setRatioX] = useState<number>(1);
  const [ratioY, setRatioY] = useState<number>(1);
  const [playerClicked, setPlayerClicked] = useState<number | null>(null);
  const doubleClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [actualHalfView, setActualHalfView] = useState<boolean>(halfView);
  const [currentPlayName, setCurrentPlayName] = useState<string | null>(null);

  const itemPlacements = useMemo(() => {
    if (!poolHeight || !poolWidth || !ratioX || !ratioY) {
      return null;
    }

    // 9 m free-throw arc width in half view (horizontal / across axis -> ratioX),
    // capped at the court width and centred on the goal.
    const freeShotAreaWidthAtHalfView =
      Math.min(GOAL_WIDTH + FREE_SHOT_AREA_MIN_RADIUS * 2, poolHeight) * ratioX;
    const freeShotAreaXAtHalfView = `calc(50% - ${freeShotAreaWidthAtHalfView /
      2}px)`;
    // Axis rule: x / width use ratioX (horizontal), y / height use ratioY
    // (vertical). calc(50% - N) uses ratioX in N for horizontal centring and
    // ratioY for vertical centring.
    const newItemPlacements = {
      playArea: {
        x: actualHalfView ? 0 : GOAL_LINE_X * ratioX,
        y: actualHalfView ? GOAL_LINE_X * ratioY : 0,
        width:
          (actualHalfView ? poolHeight : poolWidth - 2 * GOAL_LINE_X) * ratioX,
        height:
          (actualHalfView ? poolWidth / 2 - GOAL_LINE_X : poolHeight) * ratioY,
      },
      poolArea: {
        x: actualHalfView
          ? ((poolMaxWidth as number) - poolHeight * ratioX) / 2
          : ((poolMaxWidth as number) - poolWidth * ratioX) / 2,
        y: actualHalfView ? POOL_PADDING_Y * ratioY : POOL_PADDING_X * ratioY,
        width: (actualHalfView ? poolHeight : poolWidth) * ratioX,
        height: (actualHalfView ? poolWidth / 2 : poolHeight) * ratioY,
      },
      borderDot: {
        x: actualHalfView ? 0 : (GOAL_LINE_X + 5 - LINE_WIDTH * 1) * ratioX,
        y: actualHalfView ? (GOAL_LINE_X + 5 - LINE_WIDTH * 1) * ratioY : 0,
        width: (actualHalfView ? poolHeight : LINE_WIDTH * 3) * ratioX,
        height: (actualHalfView ? LINE_WIDTH * 3 : poolHeight) * ratioY,
      },
      goalLine: {
        x: actualHalfView ? 0 : GOAL_LINE_X * ratioX,
        y: actualHalfView ? GOAL_LINE_X * ratioY : 0,
        width: actualHalfView ? poolHeight * ratioX : LINE_WIDTH * ratioX,
        height: actualHalfView ? LINE_WIDTH * ratioY : poolHeight * ratioY,
      },
      // only exists when actualHalfView = false
      halfLine: {
        x: `calc(50% - ${OUTER_LINE_WIDTH * ratioX}px)`,
        y: OUTER_LINE_WIDTH * ratioY,
        width: OUTER_LINE_WIDTH * ratioX,
        height: (poolHeight - OUTER_LINE_WIDTH) * ratioY,
      },
      goal: {
        x: actualHalfView
          ? `calc(50% - ${(GOAL_WIDTH / 2) * ratioX}px)`
          : `${OUTER_LINE_WIDTH * ratioX}px`,
        y: actualHalfView
          ? '0px'
          : `calc(50% - ${(GOAL_WIDTH / 2) * ratioY}px)`,
        width: actualHalfView ? GOAL_WIDTH * ratioX : GOAL_DEPTH * ratioX,
        height: actualHalfView
          ? (GOAL_DEPTH + LINE_WIDTH) * ratioY
          : GOAL_WIDTH * ratioY,
      },
      behindGoal: {
        x: actualHalfView
          ? `calc(50% - ${GOAL_AREA_RADIUS * ratioX}px)`
          : `-${(GOAL_DEPTH - OUTER_LINE_WIDTH) * ratioX}px`,
        y: actualHalfView
          ? `-${(GOAL_DEPTH - OUTER_LINE_WIDTH) * ratioY}px`
          : `calc(50% - ${GOAL_AREA_RADIUS * ratioY}px)`,
        width: actualHalfView
          ? GOAL_AREA_RADIUS * 2 * ratioX
          : 2 * GOAL_DEPTH * ratioX,
        height: actualHalfView
          ? 2 * GOAL_DEPTH * ratioY
          : GOAL_AREA_RADIUS * 2 * ratioY,
      },
      goalArea: {
        // Half view: spans GOAL_WIDTH + 2 x radius (two quarter-circles around
        // the posts + the 3 m straight segment between them), centred.
        x: actualHalfView
          ? `calc(50% - ${((GOAL_WIDTH + GOAL_AREA_RADIUS * 2) / 2) *
              ratioX}px)`
          : `${(GOAL_LINE_X + OUTER_LINE_WIDTH) * ratioX}px`,
        y: actualHalfView
          ? `${(GOAL_LINE_X + OUTER_LINE_WIDTH) * ratioY}px`
          : `calc(50% - ${((GOAL_WIDTH + GOAL_AREA_RADIUS * 2) / 2) *
              ratioY}px)`,
        width: actualHalfView
          ? (GOAL_WIDTH + GOAL_AREA_RADIUS * 2) * ratioX
          : GOAL_AREA_RADIUS * ratioX,
        // Full view spans GOAL_WIDTH + 2 x radius on the across (vertical) axis,
        // so the two quarter-arcs around the posts leave a straight 3 m segment
        // between them (matches the half-view geometry, mirrored).
        height: actualHalfView
          ? GOAL_AREA_RADIUS * ratioY
          : (GOAL_WIDTH + GOAL_AREA_RADIUS * 2) * ratioY,
      },
      freeShotArea: {
        x: actualHalfView
          ? freeShotAreaXAtHalfView
          : `${(GOAL_LINE_X + OUTER_LINE_WIDTH) * ratioX}px`,
        y: actualHalfView
          ? `${(GOAL_LINE_X + OUTER_LINE_WIDTH) * ratioY}px`
          : `calc(50% - ${FREE_SHOT_AREA_MAX_RADIUS * ratioY}px)`,
        width: actualHalfView
          ? freeShotAreaWidthAtHalfView
          : FREE_SHOT_AREA_MIN_RADIUS * ratioX,
        height: actualHalfView
          ? FREE_SHOT_AREA_MIN_RADIUS * ratioY
          : FREE_SHOT_AREA_MAX_RADIUS * 2 * ratioY,
      },
      penaltyShotLine: {
        x: actualHalfView
          ? `calc(50% - ${(PENALTY_SHOT_LINE_LENGTH / 2) * ratioX}px)`
          : `${(PENALTY_SHOT_LINE_DISTANCE + OUTER_LINE_WIDTH + GOAL_LINE_X) *
              ratioX}px`,
        y: actualHalfView
          ? `${(PENALTY_SHOT_LINE_DISTANCE + OUTER_LINE_WIDTH + GOAL_LINE_X) *
              ratioY}px`
          : `calc(50% - ${(PENALTY_SHOT_LINE_LENGTH / 2) * ratioY}px)`,
        width: actualHalfView
          ? PENALTY_SHOT_LINE_LENGTH * ratioX
          : LINE_WIDTH * ratioX,
        height: actualHalfView
          ? LINE_WIDTH * ratioY
          : PENALTY_SHOT_LINE_LENGTH * ratioY,
      },
      goalAreaShotLine: {
        x: actualHalfView
          ? `calc(50% - ${(GOALAREA_SHOT_LINE_LENGTH / 2) * ratioX}px)`
          : `${(GOALAREA_SHOT_LINE_DISTANCE + OUTER_LINE_WIDTH + GOAL_LINE_X) *
              ratioX}px`,
        y: actualHalfView
          ? `${(GOALAREA_SHOT_LINE_DISTANCE + OUTER_LINE_WIDTH + GOAL_LINE_X) *
              ratioY}px`
          : `calc(50% - ${(GOALAREA_SHOT_LINE_LENGTH / 2) * ratioY}px)`,
        width: actualHalfView
          ? GOALAREA_SHOT_LINE_LENGTH * ratioX
          : LINE_WIDTH * ratioX,
        height: actualHalfView
          ? LINE_WIDTH * ratioY
          : GOALAREA_SHOT_LINE_LENGTH * ratioY,
      },
      player: {
        x: 0,
        y: 0,
        width: PLAYER_SIZE * ratio,
        height: PLAYER_SIZE * ratio,
      },
    };
    return newItemPlacements;
  }, [actualHalfView, poolHeight, poolWidth, ratio, ratioX, ratioY, poolMaxWidth]);

  useEffect(() => {
    // Two-axis fill: stretch the court INDEPENDENTLY on each axis so it fills
    // the whole viewport (no letter-boxing / black bands). ratioX scales the
    // horizontal axis, ratioY the vertical one. The 6 m / 9 m areas therefore
    // render as ellipses (accepted by spec). Player tokens use the smaller,
    // uniform `ratio` so they stay perfectly round, while their POSITIONS use
    // ratioX / ratioY so they sit exactly on the (stretched) lines.
    const newRatioX = actualHalfView
      ? (poolMaxWidth as number) / (POOL_HEIGHT + 2 * POOL_PADDING_X)
      : (poolMaxWidth as number) / POOL_FULL_WIDTH;
    const newRatioY = actualHalfView
      ? (poolMaxHeight as number) / (POOL_FULL_WIDTH / 2 + 2 * POOL_PADDING_Y)
      : (poolMaxHeight as number) / POOL_FULL_HEIGHT;

    setRatioX(newRatioX);
    setRatioY(newRatioY);
    setRatio(Math.min(newRatioX, newRatioY));

    // Court metres stay fixed; the stretch lives entirely in ratioX / ratioY.
    setPoolHeight(POOL_HEIGHT);
    setPoolWidth(POOL_WIDTH);
    setPositionAdjustment(1);
  }, [poolMaxHeight, actualHalfView, poolMaxWidth]);

  // ----- Path recording (movement mode PATH)
  // Trail buffered in a ref: the position-derived Draggable keys remount
  // every piece right after onStop commits the snapshot, and per-sample store
  // writes would flood the Cache API via $tactic.subscribe. The buffer is
  // keyed by piece so simultaneous multi-touch drags cannot interleave
  // samples — the last drag to start owns the buffer, the other piece falls
  // back to linear-drag semantics.
  const dragTrailRef = useRef<{ pieceKey: string; points: Position[] } | null>(
    null,
  );
  const liveTrailUpdatedAtRef = useRef<number>(0);
  const [liveTrail, setLiveTrail] = useState<LiveTrail | null>(null);

  const getPiecePosition = (kind: PieceKind, index: number): Position =>
    kind === 'ball' ? snapshot[kind] : snapshot[kind][index];

  // Inverse of the piece placement math: accumulated drag offset (px) back to
  // court metres, per axis (ratioX horizontal, ratioY vertical).
  const dragOffsetToPoolPosition = (
    base: Position,
    offsetX: number,
    offsetY: number,
  ): Position =>
    actualHalfView
      ? {
          // half view: x = across (horizontal -> ratioX), y = depth (vertical -> ratioY)
          x: (base.x * ratioX + offsetX) / ratioX,
          y: (base.y * ratioY + offsetY) / ratioY,
        }
      : {
          // full view: x = across (vertical -> ratioY), y = length (horizontal -> ratioX,
          // anchored via `right` so a rightward drag decreases it)
          x: (base.x * ratioY + offsetY) / ratioY,
          y: (base.y * ratioX - offsetX) / ratioX,
        };

  const isRecordingPaths = movementMode === MovementMode.PATH && !isStatic;

  const handleDragStart = (kind: PieceKind, index: number) => () => {
    // Clear any trail orphaned by a cancelled drag (touchcancel, mid-drag
    // remount) — a no-op re-render when there is nothing to clear.
    setLiveTrail(null);
    if (!isRecordingPaths) {
      dragTrailRef.current = null;
      return;
    }
    const base = getPiecePosition(kind, index);
    dragTrailRef.current = {
      pieceKey: `${kind}-${index}`,
      points: [{ x: base.x, y: base.y }],
    };
    liveTrailUpdatedAtRef.current = 0;
  };

  const handleDrag = (kind: PieceKind, index: number) => (event, ui) => {
    if (!isRecordingPaths) {
      return;
    }
    const trail = dragTrailRef.current;
    if (!trail || trail.pieceKey !== `${kind}-${index}`) {
      return;
    }
    const base = getPiecePosition(kind, index);
    const position = dragOffsetToPoolPosition(base, ui.x, ui.y);
    const last = trail.points[trail.points.length - 1];
    if (last) {
      const dx = position.x - last.x;
      const dy = position.y - last.y;
      if (dx * dx + dy * dy < MIN_SAMPLE_DISTANCE * MIN_SAMPLE_DISTANCE) {
        return;
      }
    }
    trail.points.push(position);
    // Throttle the visible trail: pushing state per pointer move would
    // re-render the whole court at pointer rate (iPad main thread).
    const now = Date.now();
    if (now - liveTrailUpdatedAtRef.current >= 80) {
      liveTrailUpdatedAtRef.current = now;
      setLiveTrail({ kind, points: [...trail.points] });
    }
  };

  const handleDragStop = (kind: PieceKind, index: number) => (event, ui) => {
    const base = getPiecePosition(kind, index);

    const newPositionlist =
      kind === 'ball' ? [snapshot[kind]] : [...snapshot[kind]];

    const newPosition = dragOffsetToPoolPosition(base, ui.lastX, ui.lastY);

    newPositionlist[kind === 'ball' ? 0 : index] = newPosition;

    const nextSnapshot: Snapshot = {
      ...snapshot,
      [kind]: kind === 'ball' ? newPositionlist[0] : newPositionlist,
    };

    if (!isStatic) {
      const buffered = dragTrailRef.current;
      const ownsTrail = !!buffered && buffered.pieceKey === `${kind}-${index}`;
      if (ownsTrail) {
        dragTrailRef.current = null;
      }
      setLiveTrail(null);
      // A tap fires onStop with a zero delta; it must not rewrite or clear
      // recorded paths — only an actual drag does.
      const moved = Math.abs(ui.lastX) > 2 || Math.abs(ui.lastY) > 2;
      if (moved) {
        nextSnapshot.paths = applyDragToPathSet(
          snapshot.paths,
          kind,
          index,
          ownsTrail ? buffered!.points : [],
          newPosition,
          isRecordingPaths,
        );
      }
    }

    onSnapshotUpdate(nextSnapshot);
  };

  // Court-metre position -> screen px of the PIECE CENTRE, mirroring the
  // piece divs' placement math including the per-axis disk-centring nudges
  // (the ball is a half-size disk, so its nudge and half-extent are halved).
  const poolPositionToScreen = (
    position: Position,
    kind: PieceKind,
  ): { x: number; y: number } => {
    const sizeFactor = kind === 'ball' ? 0.5 : 1;
    const half = (PLAYER_SIZE * ratio * sizeFactor) / 2;
    const centreOffsetX = (PLAYER_SIZE / 2) * (ratioX - ratio) * sizeFactor;
    const centreOffsetY = (PLAYER_SIZE / 2) * (ratioY - ratio) * sizeFactor;
    if (actualHalfView) {
      return {
        x: position.x * ratioX + centreOffsetX + half,
        y: position.y * ratioY + centreOffsetY + half,
      };
    }
    const poolScreenWidth = (itemPlacements?.poolArea.width as number) || 0;
    return {
      x: poolScreenWidth - (position.y * ratioX + centreOffsetX) - half,
      y: position.x * ratioY + centreOffsetY + half,
    };
  };

  const onPlayerClick = (index) => {
    if (namingEditorActive && onNamingPlayer) {
      return () => !!onNamingPlayer && onNamingPlayer(index);
    }
    return onDoubleClick(index);
  };

  const onDoubleClick = (index) => () => {
    if (playerClicked === index) {
      clearTimeout(doubleClickTimer.current!);
      setPlayerClicked(null);
      onStartNamingPlayer && onStartNamingPlayer(index);
    }
    setPlayerClicked(index);
    clearTimeout(doubleClickTimer.current!);
    doubleClickTimer.current = setTimeout(() => {
      setPlayerClicked(null);
    }, 400);
  };

  // If there's any elemnent (player, opp, ball) in the snapshot
  // outside the half pool, we rotate the screen to full view
  useEffect(() => {
    // if we're already in full view, we don't need to do anything
    setActualHalfView(halfView);
    if (!halfView) {
      return;
    }
    // if we're not in the sidebar we trust the user to set the correct view
    if (!isStatic) {
      return;
    }
    const isElementOutsideHalfPool = (position: Position) => {
      return (position.y as number) >= POOL_WIDTH / 2;
    };
    const elements = [
      ...snapshot.players,
      ...snapshot.opponents,
      snapshot.ball,
    ];
    const isAnyElementOutsideHalfPool = elements.some(isElementOutsideHalfPool);
    setActualHalfView(!isAnyElementOutsideHalfPool);
  }, [snapshot, halfView, isStatic]);

  useEffect(() => {
    if (playName !== currentPlayName) {
      setCurrentPlayName(playName);
    }
  }, [playName, currentPlayName]);

  if (!itemPlacements) {
    return null;
  }

  // Disk-centring correction. Disks stay ROUND (size = PLAYER_SIZE * ratio,
  // where ratio = min(ratioX, ratioY)), but their positions scale per-axis
  // (ratioX / ratioY). A stored coord is the disk's TOP-LEFT and the disk is
  // PLAYER_SIZE wide, so its intended CENTRE sits +PLAYER_SIZE/2 metres in.
  // With a round disk that offset is PLAYER_SIZE/2 * ratio px, but the target
  // centre on each axis is at PLAYER_SIZE/2 * ratioX|Y — so on non-square
  // viewports (ratioX != ratioY) every disk drifts by the difference, shifting
  // the whole formation off the court. These per-axis nudges cancel that drift
  // so disks centre correctly at any aspect ratio. (Added as a constant offset,
  // so the delta-based drag maths in handleDragStop is unaffected.)
  const centreOffsetX = (PLAYER_SIZE / 2) * (ratioX - ratio);
  const centreOffsetY = (PLAYER_SIZE / 2) * (ratioY - ratio);

  return (
    <Pool
      $placement={itemPlacements.poolArea}
      $ratio={ratio}
      $halfView={actualHalfView}
      $isDrawingEnabled={isDrawingEnabled}
      $colorMode={colorMode}
      key={`pool-${currentPlayName || ''}-2`}
      ref={(poolRef as unknown) as React.RefObject<HTMLDivElement>}
    >
      {isDrawingEnabled && !isStatic && (
        <PositionedDrawingCanvas
          $placement={itemPlacements.playArea}
          $isActive={drawingMode !== DrawingMode.NONE}
          $ratio={ratio}
          width={itemPlacements.playArea.width as number}
          height={itemPlacements.playArea.height as number}
          mode={drawingMode}
          lineWidth={LINE_WIDTH * ratio * 1.5}
          shouldCleanDrawings={shouldCleanDrawings}
          onDrawingsCleaned={onDrawingsCleaned}
          shouldUndoLastDrawing={shouldUndoLastDrawing}
          onLastDrawingUndone={onLastDrawingUndone}
        />
      )}
      <PlayArea
        $placement={itemPlacements.playArea}
        $ratio={ratio}
        $halfView={actualHalfView}
        $isDrawingEnabled={isDrawingEnabled}
        key={`pool-${currentPlayName || ''}`}
        $isDarkMode={colorMode === 'dark'}
      />
      {!isStatic && (liveTrail || (showSavedPaths && snapshot.paths)) && (
        <PathOverlay
          width={itemPlacements.poolArea.width as number}
          height={itemPlacements.poolArea.height as number}
          strokeWidth={LINE_WIDTH * ratio * 1.5}
          toScreen={poolPositionToScreen}
          liveTrail={liveTrail}
          savedPaths={showSavedPaths ? snapshot.paths : null}
        />
      )}
      {/* {!isStatic && !backgroundText && <LogoImage src={logo} alt="Logo" />} */}
      {/* {!!backgroundText && (
        <BackgroundText
          $maxHeight={poolMaxHeight}
          $centerOffset={
            actualHalfView ? (itemPlacements.goal.height as number) : 0
          }
        >
          {backgroundText}
        </BackgroundText>
      )} */}
      {snapshot['players'].map((value, index) => {
        const capNumber = namings?.[index] || '';
        const isPlayerSelected = selectedPlayer === index;
        return (
          <Draggable
            disabled={isStatic || namingEditorActive}
            onStart={handleDragStart('players', index)}
            onDrag={handleDrag('players', index)}
            onStop={handleDragStop('players', index)}
            key={`players-${value.x}-${value.y}`}
          >
            <Player
              onClick={onPlayerClick(index)}
              onTouchStart={onPlayerClick(index)}
              $placement={itemPlacements.player}
              $halfView={actualHalfView}
              $origin={POOL_HEIGHT * ratio}
              $selected={isPlayerSelected}
              style={
                actualHalfView
                  ? {
                      top: (value.y as number) * ratioY + centreOffsetY,
                      left: (value.x as number) * ratioX + centreOffsetX,
                    }
                  : {
                      right: (value.y as number) * ratioX + centreOffsetX,
                      top: (value.x as number) * ratioY + centreOffsetY,
                    }
              }
              $number={capNumber}
            >
              <PlayerOwnDisk
                style={{ width: '100%' }}
                key={`player-own-${index}`}
              />
            </Player>
          </Draggable>
        );
      })}
      {snapshot['opponents'].map((value, index) => {
        const opponentIndex = index + opponentIncrease;
        const capNumber = namings?.[opponentIndex] || '';
        const isPlayerSelected = selectedPlayer === opponentIndex;
        return (
          <Draggable
            disabled={isStatic || namingEditorActive}
            onStart={handleDragStart('opponents', index)}
            onDrag={handleDrag('opponents', index)}
            onStop={handleDragStop('opponents', index)}
            key={`opponents-${value.x}-${value.y}`}
          >
            <Player
              className={'plr'}
              onClick={onPlayerClick(opponentIndex)}
              onTouchStart={onPlayerClick(opponentIndex)}
              $placement={itemPlacements.player}
              $halfView={actualHalfView}
              $origin={POOL_HEIGHT * ratio}
              $selected={isPlayerSelected}
              $number={capNumber}
              style={
                actualHalfView
                  ? {
                      top: (value.y as number) * ratioY + centreOffsetY,
                      left: (value.x as number) * ratioX + centreOffsetX,
                    }
                  : {
                      right: (value.y as number) * ratioX + centreOffsetX,
                      top: (value.x as number) * ratioY + centreOffsetY,
                    }
              }
            >
              <PlayerOpponentDisk
                style={{ width: '100%' }}
                className={'player-opponent'}
                key={`player-opponent-${index}`}
              />
            </Player>
          </Draggable>
        );
      })}
      {snapshot['goalkeepers'][0] && (
        <Draggable
          disabled={isStatic || namingEditorActive}
          onStart={handleDragStart('goalkeepers', 0)}
          onDrag={handleDrag('goalkeepers', 0)}
          onStop={handleDragStop('goalkeepers', 0)}
          key={`goalkeepers-${snapshot['goalkeepers'][0].x}-${snapshot['goalkeepers'][0].y}`}
        >
          <Player
            $placement={itemPlacements.player}
            $halfView={!!actualHalfView}
            $origin={POOL_HEIGHT * ratio}
            style={
              actualHalfView
                ? {
                    top:
                      (snapshot['goalkeepers'][0].y as number) * ratioY +
                      centreOffsetY,
                    left:
                      (snapshot['goalkeepers'][0].x as number) * ratioX +
                      centreOffsetX,
                  }
                : {
                    right:
                      (snapshot['goalkeepers'][0].y as number) * ratioX +
                      centreOffsetX,
                    top:
                      (snapshot['goalkeepers'][0].x as number) * ratioY +
                      centreOffsetY,
                  }
            }
          >
            <GoalKeeperDisk style={{ width: '100%' }} />
          </Player>
        </Draggable>
      )}
      {snapshot['goalkeepers'][1] && (
        <Draggable
          disabled={isStatic || namingEditorActive}
          onStart={handleDragStart('goalkeepers', 1)}
          onDrag={handleDrag('goalkeepers', 1)}
          onStop={handleDragStop('goalkeepers', 1)}
          key={`goalkeepers-${snapshot['goalkeepers'][1].x}-${snapshot['goalkeepers'][1].y}`}
        >
          <Player
            $placement={itemPlacements.player}
            $halfView={!!actualHalfView}
            $origin={POOL_HEIGHT * ratio}
            style={
              actualHalfView
                ? {
                    top:
                      (snapshot['goalkeepers'][1].y as number) * ratioY +
                      centreOffsetY,
                    left:
                      (snapshot['goalkeepers'][1].x as number) * ratioX +
                      centreOffsetX,
                  }
                : {
                    right:
                      (snapshot['goalkeepers'][1].y as number) * ratioX +
                      centreOffsetX,
                    top:
                      (snapshot['goalkeepers'][1].x as number) * ratioY +
                      centreOffsetY,
                  }
            }
          >
            <GoalKeeperDisk style={{ width: '100%' }} />
          </Player>
        </Draggable>
      )}
      <Draggable
        disabled={isStatic || namingEditorActive}
        onStart={handleDragStart('ball', 0)}
        onDrag={handleDrag('ball', 0)}
        onStop={handleDragStop('ball', 0)}
        key={`ball-${snapshot['ball'].x}-${snapshot['ball'].y}`}
      >
        <Ball
          $placement={itemPlacements.player}
          $halfView={actualHalfView}
          $origin={POOL_HEIGHT * ratio}
          style={
            actualHalfView
              ? {
                  // The ball is a half-size disk, so its centring offset is
                  // half of a player's.
                  top:
                    (snapshot['ball'].y as number) * ratioY +
                    centreOffsetY / 2,
                  left:
                    (snapshot['ball'].x as number) * ratioX +
                    centreOffsetX / 2,
                }
              : {
                  right:
                    (snapshot['ball'].y as number) * ratioX +
                    centreOffsetX / 2,
                  top:
                    (snapshot['ball'].x as number) * ratioY +
                    centreOffsetY / 2,
                }
          }
        >
          <BallDisk style={{ width: '100%' }} />
        </Ball>
      </Draggable>
      <Goal
        $placement={itemPlacements.goal}
        $ratio={ratio}
        $reverse={false}
        $halfView={actualHalfView}
      />
      <BehindGoal
        $placement={itemPlacements.behindGoal}
        $ratio={ratio}
        $reverse={false}
        $halfView={actualHalfView}
      />
      <ShotLineYellow
        $placement={itemPlacements.penaltyShotLine}
        $ratio={ratio}
        $halfView={actualHalfView}
        $reverse={false}
      />
      <ShotLineOrange
        $placement={itemPlacements.goalAreaShotLine}
        $ratio={ratio}
        $halfView={actualHalfView}
        $reverse={false}
      />
      <GoalArea
        $placement={itemPlacements.goalArea}
        $reverse={false}
        $ratio={ratio}
        $radiusX={GOAL_AREA_RADIUS * ratioX}
        $radiusY={GOAL_AREA_RADIUS * ratioY}
        $halfView={actualHalfView}
        $colorMode={colorMode}
      />
      <FreeShotArea
        $placement={itemPlacements.freeShotArea}
        $reverse={false}
        $ratio={ratio}
        $radiusX={FREE_SHOT_AREA_MIN_RADIUS * ratioX}
        $radiusY={FREE_SHOT_AREA_MIN_RADIUS * ratioY}
        $halfView={actualHalfView}
      />
      {!actualHalfView && (
        <>
          <GoalArea
            $placement={itemPlacements.goalArea}
            $reverse
            $ratio={ratio}
            $radiusX={GOAL_AREA_RADIUS * ratioX}
            $radiusY={GOAL_AREA_RADIUS * ratioY}
            $halfView={actualHalfView}
            $colorMode={colorMode}
          />
          <Goal
            $placement={itemPlacements.goal}
            $ratio={ratio}
            $reverse
            $halfView={actualHalfView}
          />
          <BehindGoal
            $placement={itemPlacements.behindGoal}
            $ratio={ratio}
            $reverse
            $halfView={actualHalfView}
          />
          <FreeShotArea
            $placement={itemPlacements.freeShotArea}
            $reverse
            $ratio={ratio}
            $radiusX={FREE_SHOT_AREA_MIN_RADIUS * ratioX}
            $radiusY={FREE_SHOT_AREA_MIN_RADIUS * ratioY}
            $halfView={actualHalfView}
          />
          <ShotLineYellow
            $placement={itemPlacements.penaltyShotLine}
            $ratio={ratio}
            $halfView={actualHalfView}
            $reverse
          />
          <ShotLineOrange
            $placement={itemPlacements.goalAreaShotLine}
            $ratio={ratio}
            $halfView={actualHalfView}
            $reverse
          />
          <Line
            $placement={itemPlacements.halfLine}
            $ratio={ratio}
            $halfView={actualHalfView}
            $reverse={false}
            $color={white}
          />
        </>
      )}
    </Pool>
  );
};
